export const ONBOARDING_STEPS = [
	'FORM',
	'SMS_VERIFICATION',
	'PAYMENT',
	'KYC',
	'IN_REVIEW',
	'COMPLETED',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const PAYMENT_STATUSES = ['NONE', 'PENDING', 'PAID'] as const;

export type OnboardingPaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * Response of GET /v2/onboarding/{ulid}/status. `step` is kept as a string so
 * a value added by the backend later is reported instead of failing the guard.
 */
export type OnboardingStatus = {
	onboarding_ulid: string;
	step: string;
	payment_required: boolean;
	payment_status: OnboardingPaymentStatus;
	checkout_id: string | null;
	// Recurrente URL of checkout_id while PENDING, so another browser can
	// reopen the same checkout.
	checkout_url: string | null;
	product_id: string | null;
	has_active_certificate: boolean;
	process_status: string | null;
	// Request details shown while the process is in review.
	process: OnboardingProcessDetails | null;
};

/** Every field may be missing or null; render only what is present. */
export type OnboardingProcessDetails = {
	cert_type?: string | null;
	serial?: string | null;
	created_at?: string | null;
	status?: string | null;
	paid_at?: string | null;
	product_id?: string | null;
	payment_processor?: string | null;
	kyc_completed_at?: string | null;
};

type OptionalStatusField =
	| 'checkout_id'
	| 'checkout_url'
	| 'product_id'
	| 'process_status'
	| 'process';

/** The status as sent: optional fields may be missing. */
export type OnboardingStatusResponse = Omit<
	OnboardingStatus,
	OptionalStatusField
> &
	Partial<Pick<OnboardingStatus, OptionalStatusField>>;

/** Wizard indices rendered by request-signature.tsx. */
export const WIZARD_STEP = {
	START: 0,
	AGREEMENT: 1,
	FORM: 2,
	SUMMARY: 3,
	SMS_VERIFICATION: 4,
	PAYMENT: 5,
	KYC: 6,
} as const;

export type OnboardingDestination =
	| { kind: 'wizard'; step: number }
	| { kind: 'in_review' }
	| { kind: 'dashboard' }
	| { kind: 'unknown' };

export function isOnboardingStep(value: unknown): value is OnboardingStep {
	return (
		typeof value === 'string' &&
		(ONBOARDING_STEPS as readonly string[]).includes(value)
	);
}

// The optional fields are left out of the response when unset.
const isOptionalString = (value: unknown) =>
	value === undefined || value === null || typeof value === 'string';

export function isOnboardingStatus(
	value: unknown,
): value is OnboardingStatusResponse {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const status = value as Record<string, unknown>;

	return (
		typeof status.onboarding_ulid === 'string' &&
		typeof status.step === 'string' &&
		typeof status.payment_required === 'boolean' &&
		(PAYMENT_STATUSES as readonly unknown[]).includes(
			status.payment_status,
		) &&
		isOptionalString(status.checkout_id) &&
		isOptionalString(status.checkout_url) &&
		isOptionalString(status.product_id) &&
		typeof status.has_active_certificate === 'boolean' &&
		isOptionalString(status.process_status) &&
		isOptionalProcessDetails(status.process)
	);
}

// The details are informational, so only their shape is checked: a value of
// an unexpected type is dropped by the screen instead of failing the status.
function isOptionalProcessDetails(value: unknown): boolean {
	return value === undefined || value === null || typeof value === 'object';
}

/** Checkout ids of redeemed promo codes; there is no Recurrente checkout. */
export const isPromoCheckoutId = (checkoutId: string) =>
	checkoutId.startsWith('PROMO#');

/** Where a backend step sends the user. */
export function getOnboardingDestination(step: string): OnboardingDestination {
	if (!isOnboardingStep(step)) {
		return { kind: 'unknown' };
	}

	switch (step) {
		case 'FORM':
			return { kind: 'wizard', step: WIZARD_STEP.FORM };
		case 'SMS_VERIFICATION':
			return { kind: 'wizard', step: WIZARD_STEP.SMS_VERIFICATION };
		case 'PAYMENT':
			return { kind: 'wizard', step: WIZARD_STEP.PAYMENT };
		case 'KYC':
			return { kind: 'wizard', step: WIZARD_STEP.KYC };
		case 'IN_REVIEW':
			return { kind: 'in_review' };
		case 'COMPLETED':
			return { kind: 'dashboard' };
	}
}

/**
 * Lowest wizard step the user may navigate back to once the backend reports
 * `step`. Before the phone is confirmed the personal data can still be edited
 * (data-enrichment accepts it while the process is in phone_validation); once
 * init-kyc has run, or the process is paid, earlier steps are closed.
 */
export function getOnboardingFloor(step: string): number {
	switch (step) {
		case 'SMS_VERIFICATION':
			return WIZARD_STEP.FORM;
		case 'PAYMENT':
			return WIZARD_STEP.PAYMENT;
		case 'KYC':
			return WIZARD_STEP.KYC;
		default:
			return WIZARD_STEP.START;
	}
}

/** Steps a returning user can be put back into. */
export function isResumableStep(step: string): boolean {
	return step === 'SMS_VERIFICATION' || step === 'PAYMENT' || step === 'KYC';
}
